import React, { useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  RefreshControl,
  StatusBar,
  StyleSheet,
  Text,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { GradientBackground } from '../GradientBackground';
import { PageHeader } from '../PageHeader';
import { NewsfeedThreadCard } from './NewsfeedThreadCard';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import {
  ThreadCardSkeleton,
  CorkBoardEmptyState,
} from '@/components/residents/corkBoard';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

const HP = REDESIGN_SPACING.horizontalPadding;

interface NewsfeedUserListScreenProps {
  title: string;
  subtitle: string;
  screenName: string;
  threads: CorkBoardThreadSummary[];
  isInitialLoad: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  onRefresh: () => void;
  onEndReached: () => void;
  onThreadPress: (thread: CorkBoardThreadSummary) => void;
  onBack: () => void;
  emptyStateVariant: 'myThreads' | 'savedThreads';
  onCreatePost?: () => void;
  onBrowseFeed?: () => void;
  onSave?: (thread: CorkBoardThreadSummary) => void;
  onEdit?: (thread: CorkBoardThreadSummary) => void;
  onDelete?: (thread: CorkBoardThreadSummary) => void;
  onReport?: (thread: CorkBoardThreadSummary) => void;
}

export const NewsfeedUserListScreen: React.FC<NewsfeedUserListScreenProps> = React.memo(({
  title,
  subtitle,
  screenName,
  threads,
  isInitialLoad,
  isRefreshing,
  isLoadingMore,
  isError,
  onRefresh,
  onEndReached,
  onThreadPress,
  onBack,
  emptyStateVariant,
  onCreatePost,
  onBrowseFeed,
  onSave,
  onEdit,
  onDelete,
  onReport,
}) => {
  const { colors } = useThemeColors();
  const { layoutMode } = useResponsive();
  const insets = useSafeAreaInsets();
  const user = useAppSelector((state) => state.auth.user);
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';

  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';

  const renderThread = useCallback(({ item }: { item: CorkBoardThreadSummary }) => (
    <NewsfeedThreadCard
      thread={item}
      onPress={onThreadPress}
      onSave={onSave}
      onEdit={onEdit}
      onDelete={onDelete}
      onReport={onReport}
    />
  ), [onThreadPress, onSave, onEdit, onDelete, onReport]);

  const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

  const ListHeaderComponent = useMemo(() => (
    <View style={styles.headerSection}>
      <ScreenBackLink label="Back to Feed" onPress={onBack} />
      <PageHeader title={title} subtitle={propertyName} />
      <Text style={[styles.descriptionText, { color: colors.text.dimmer }]}>{subtitle}</Text>
    </View>
  ), [onBack, title, subtitle, propertyName, colors]);

  const ListEmptyComponent = useMemo(() => {
    if (isInitialLoad) return <ThreadCardSkeleton />;
    if (isError) return <CorkBoardEmptyState variant="error" onRetry={onRefresh} />;
    return (
      <CorkBoardEmptyState
        variant={emptyStateVariant}
        onCreatePost={onCreatePost}
        onBrowseFeed={onBrowseFeed}
      />
    );
  }, [isInitialLoad, isError, onRefresh, emptyStateVariant, onCreatePost, onBrowseFeed]);

  const ListFooterComponent = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={PRIMITIVE_COLORS.resioBlue} />
      </View>
    );
  }, [isLoadingMore]);

  const renderList = () => (
    <FlatList
      data={threads}
      renderItem={renderThread}
      keyExtractor={keyExtractor}
      ListHeaderComponent={ListHeaderComponent}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      contentContainerStyle={[
        styles.listContentContainer,
        { paddingBottom: 40 + insets.bottom },
      ]}
      ItemSeparatorComponent={ItemSeparator}
      onEndReached={onEndReached}
      onEndReachedThreshold={0.4}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews={Platform.OS !== 'web'}
      maxToRenderPerBatch={10}
      windowSize={5}
      initialNumToRender={10}
      updateCellsBatchingPeriod={50}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={onRefresh}
          tintColor={colors.text.primary}
        />
      }
    />
  );

  if (isMobile || isTablet) {
    return (
      <GradientBackground>
        <StatusBar barStyle="light-content" />
        <BaseResidentMobileScreen
          screenName={screenName}
          darkBackground
          headerVariant="gradient"
        >
          <View style={styles.feedContainer}>
            {renderList()}
          </View>
        </BaseResidentMobileScreen>
      </GradientBackground>
    );
  }

  return (
    <View style={styles.desktopContainer}>
      <View style={styles.desktopWrapper}>
        {renderList()}
      </View>
    </View>
  );
});

NewsfeedUserListScreen.displayName = 'NewsfeedUserListScreen';

const ItemSeparator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  feedContainer: {
    flex: 1,
  },
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  desktopWrapper: {
    flex: 1,
    maxWidth: 960,
    alignSelf: 'center' as const,
    width: '100%',
    paddingHorizontal: 40,
    paddingTop: 24,
  },
  listContentContainer: {
    paddingHorizontal: HP,
  },
  headerSection: {
    gap: 4,
    paddingBottom: 16,
  },
  descriptionText: {
    ...REDESIGN_TYPOGRAPHY.small,
    marginTop: 2,
  },
  separator: {
    height: 12,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
});
